import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Icon, Select, Tag } from '@repo/design-system';
import { fontMono, ink, space, typeRole } from '@repo/design-tokens';
import { countryZones, sameZone } from '@repo/front-end-utils';
import type { ZoneDerivation } from '@repo/front-end-utils';
import type { PositionSource, TimezoneSource } from '@repo/frontend-types';
import { IconDisc } from '@repo/ui';

import type { GeocodeState } from './useAddressGeocode';
import { formatStoredNumber } from './positionOnMapRows';
import PositionControls from './PositionControls';
import ZonePicker from './ZonePicker';
import {
  PLACEHOLDER_ORIGIN,
  isLocated,
  looksLikePlaceholderOrigin,
  type TwinDetailsErrors,
  type TwinPosition,
} from './twinDetailsForm';
import { TIMEZONE_SOURCE_LABEL, positionSourceTag } from './twinValueSources';

/**
 * Twin details' right panel (`twin-details-page-address`, R1, R4, R5, R7; frame 02): *Worked out
 * from the address*, the timezone card, the position card and *Next: Opening Hours*.
 *
 * The cards are app compositions of design-system parts (epic Assessment: *the derived-value
 * cards*). Frame 02 draws the derived states; the *choose* and *unknown* timezone states, the
 * *Change* list, *The address points to…*, the position card with no position or with *Enter
 * coordinates* open, and *Find latitude & longitude* inside the card are undrawn, built from the
 * same parts, for Rob to ratify after the merge (spec Human Action 3).
 */

const USE_IT = 'Opening hours and daily figures use it.';

/**
 * R4.4: where the shown zone came from, in one line — read from its SOURCE, so the line and the
 * source tag beside it can never disagree. A zone from the country says whether the country was
 * enough on its own (one zone) or its main zone was taken (DE, ES, PT); a kept zone (entered, set in
 * Edit mode, or not recorded) says only what it is used for.
 *
 * A worked-out sentence is given only while the address still gives that zone. A zone kept after
 * the country or position changed (R4.3: the manage route, or a country to choose in) reads as
 * kept, never as the new country's: a country zone must be one of the country's, and the main-zone
 * and position sentences need the answer (once settled) to name it.
 */
export const reasonLine = (
  source: TimezoneSource | null | undefined,
  zone: string,
  countryCode: string | null,
  countryLabel: string | null,
  answer: ZoneDerivation | null,
): string => {
  // The address still gives the shown zone: no answer yet, or a derived one naming it.
  const stillGiven = answer === null || (answer.kind === 'derived' && sameZone(answer.zone, zone));
  if (source === 'position') {
    return stillGiven ? `Worked out from the position. ${USE_IT}` : USE_IT;
  }
  if (source !== 'country' || !countryCode || !countryLabel) return USE_IT;
  const zones = countryZones(countryCode) ?? [];
  if (!zones.some(own => sameZone(own, zone))) return USE_IT;
  if (zones.length === 1) {
    return `${countryLabel} has one timezone, so the country is enough. ${USE_IT}`;
  }
  if (!stillGiven) return USE_IT;
  return `${zone} is ${countryLabel}’s main timezone. Enter the position if the site is elsewhere, or change it.`;
};

interface Props {
  className?: string;
  // Timezone (R4)
  storedZone: string;
  timezoneSource: TimezoneSource | null | undefined;
  answer: ZoneDerivation | null;
  zonePending: boolean;
  pointsElsewhere: string | null;
  countryCode: string | null;
  countryLabel: string | null;
  onZone: (zone: string) => void;
  // Position (R5)
  saved: TwinPosition;
  positionSource: PositionSource | null | undefined;
  coords: TwinPosition;
  coordErrors: TwinDetailsErrors;
  onCoordChange: (field: keyof TwinPosition, value: string) => void;
  /** Save the typed pair if it is whole and changed. `false`: held (half a pair, out of range). */
  onCoordCommit: () => boolean;
  /** Put back the saved pair (Escape). */
  onCoordRestore: () => void;
  /** The page's address lookup, whose outcome the position card shows (no *Find* here). */
  geocode: { state: GeocodeState; canSearch: boolean; disabledReason?: string };
  // Next (R7)
  onNext: () => void;
}

const TwinDetailsDerived: React.FC<Props> = ({
  className,
  storedZone,
  timezoneSource,
  answer,
  zonePending,
  pointsElsewhere,
  countryCode,
  countryLabel,
  onZone,
  saved,
  positionSource,
  coords,
  coordErrors,
  onCoordChange,
  onCoordCommit,
  onCoordRestore,
  geocode,
  onNext,
}) => {
  return (
    <section className={className} aria-labelledby="td-derived">
      <header className="panel-head">
        <IconDisc tone="aqua" size={30}>
          <Icon name="wand-magic-sparkles" variant="solid" size="xs" />
        </IconDisc>
        <div>
          <h3 id="td-derived">Worked out from the address</h3>
          <p className="sub">Fills in from the country and the position</p>
        </div>
      </header>
      <TimezoneCard
        storedZone={storedZone}
        source={timezoneSource}
        answer={answer}
        pending={zonePending}
        pointsElsewhere={pointsElsewhere}
        countryCode={countryCode}
        countryLabel={countryLabel}
        onZone={onZone}
      />
      <PositionCard
        saved={saved}
        source={positionSource}
        coords={coords}
        errors={coordErrors}
        onCoordChange={onCoordChange}
        onCoordCommit={onCoordCommit}
        onCoordRestore={onCoordRestore}
        geocode={geocode}
      />
      <div className="next">
        <Button
          variant="primary"
          size="full"
          iconRight={<Icon name="arrow-right" />}
          onClick={onNext}
        >
          Next: Opening Hours
        </Button>
      </div>
    </section>
  );
};

const TimezoneCard: React.FC<{
  storedZone: string;
  source: TimezoneSource | null | undefined;
  answer: ZoneDerivation | null;
  pending: boolean;
  pointsElsewhere: string | null;
  countryCode: string | null;
  countryLabel: string | null;
  onZone: (zone: string) => void;
}> = ({
  storedZone,
  source,
  answer,
  pending,
  pointsElsewhere,
  countryCode,
  countryLabel,
  onZone,
}) => {
  const [changing, setChanging] = useState(false);
  const changeRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef(false);
  const stored = storedZone.trim();
  // A derived answer for a twin with no zone is about to be saved (R4.3); show it now.
  const shown = stored || (answer?.kind === 'derived' ? answer.zone : '');
  const shownSource: TimezoneSource | null | undefined =
    stored || answer?.kind !== 'derived' ? source : answer.basis;
  const tag = shownSource ? TIMEZONE_SOURCE_LABEL[shownSource] : undefined;

  useEffect(() => {
    if (changing) listRef.current?.querySelector('input')?.focus();
    else if (returnFocus.current) {
      returnFocus.current = false;
      changeRef.current?.focus();
    }
  }, [changing]);

  const close = () => {
    returnFocus.current = true;
    setChanging(false);
  };

  let body: React.ReactNode;
  if (shown) {
    body = (
      <>
        <p className="value">{shown}</p>
        <p className="reason">
          {reasonLine(shownSource, shown, countryCode, countryLabel, answer)}
        </p>
        {pointsElsewhere && <p className="reason">The address points to {pointsElsewhere}.</p>}
      </>
    );
  } else if (pending) {
    body = <p className="reason">Working out the timezone…</p>;
  } else if (answer?.kind === 'choose') {
    body = (
      <>
        <p className="value">{countryLabel} has several timezones</p>
        <Select
          options={answer.candidates.map(zone => ({ value: zone, label: zone }))}
          placeholder="Choose a timezone…"
          ariaLabel="Timezone"
          onChange={zone => onZone(zone)}
        />
        <p className="reason">Enter the position to work it out, or choose one.</p>
      </>
    );
  } else if (answer?.kind === 'unknown' && answer.reason === 'unsupported-engine') {
    body = <p className="reason">This browser can’t work out the timezone. Choose it.</p>;
  } else {
    body = <p className="reason">Choose a country first</p>;
  }

  return (
    <Card surface="on-scene" size="md" className="derived-card zone-card">
      {/* One polite region over the tag, the zone and the reason, so *From country, Europe/London*
          is announced once when it is worked out (criterion 15). */}
      <div aria-live="polite" className="live">
        <div className="card-head">
          <span className="label">
            <Icon name="earth-europe" size="xs" />
            Timezone
          </span>
          {tag && shown && (
            <Tag surface="on-glass" size="dense" tone="mint">
              {tag}
            </Tag>
          )}
        </div>
        {body}
      </div>
      {changing ? (
        <div
          className="change-list"
          ref={listRef}
          onKeyDown={event => {
            // R4.6: Escape closes Change unchanged — the list opens on focus, so its own close
            // would otherwise leave the person a second Escape away.
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
            }
          }}
        >
          <ZonePicker
            countryCode={countryCode}
            countryLabel={countryLabel}
            stored={stored}
            onChoose={zone => {
              if (zone && !sameZone(zone, stored)) onZone(zone);
              close();
            }}
          />
        </div>
      ) : answer?.kind !== 'choose' || stored ? (
        <div className="actions">
          <Button
            ref={changeRef}
            variant="text"
            size="inline"
            aria-label="Change timezone"
            onClick={() => setChanging(true)}
          >
            Change
          </Button>
        </div>
      ) : null}
    </Card>
  );
};

const PositionCard: React.FC<{
  saved: TwinPosition;
  source: PositionSource | null | undefined;
  coords: TwinPosition;
  errors: TwinDetailsErrors;
  onCoordChange: (field: keyof TwinPosition, value: string) => void;
  onCoordCommit: () => boolean;
  onCoordRestore: () => void;
  geocode: { state: GeocodeState; canSearch: boolean; disabledReason?: string };
}> = ({ saved, source, coords, errors, onCoordChange, onCoordCommit, onCoordRestore, geocode }) => {
  const located = isLocated(saved);
  const placeholder = looksLikePlaceholderOrigin(saved);
  const tag = located ? positionSourceTag(source, placeholder) : undefined;

  return (
    <Card surface="on-scene" size="md" className="derived-card">
      <div className="card-head">
        <span className="label">
          <Icon name="location-dot" size="xs" />
          Position
        </span>
        {tag && (
          <Tag surface="on-glass" size="dense" tone={tag.tone}>
            {tag.text}
          </Tag>
        )}
      </div>
      {located ? (
        <p className="value mono">
          {placeholder
            ? `${saved.latitude.trim()}, ${saved.longitude.trim()}`
            : `${formatStoredNumber(saved.latitude)}, ${formatStoredNumber(saved.longitude)}`}
        </p>
      ) : (
        <p className="value">No position yet</p>
      )}
      {!located && (
        <p className="reason" role="status">
          No position set. Go live is refused rather than placing the twin at 0,0 — unless the twin
          is already live, in which case it keeps the position it is already rendered at.
        </p>
      )}
      {/* Not a block: 51.4779, 0 is a real point (the Thames estuary), and refusing a legitimate
          coordinate because one legacy default happened to use it would be a booby trap. */}
      {placeholder && (
        <p className="reason" role="status">
          This position ({PLACEHOLDER_ORIGIN.latitude}, {PLACEHOLDER_ORIGIN.longitude}) is the old
          placeholder default. It is a real point — in the Thames estuary — so it will not block Go
          live, but it is almost certainly not where this twin is. Save its address, or enter the
          real coordinates.
        </p>
      )}
      <PositionControls
        saved={saved}
        coords={coords}
        errors={errors}
        onCoordChange={onCoordChange}
        onCoordCommit={onCoordCommit}
        onCoordRestore={onCoordRestore}
        geocode={geocode}
        addressAction="none"
      />
    </Card>
  );
};

export default styled(TwinDetailsDerived)`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};

  /* *Change*'s list opens over the Position card below: each card is its own stacking context, so
     without this the later card paints over the list. */
  .zone-card {
    position: relative;
    z-index: 1;
  }

  .panel-head {
    display: flex;
    align-items: center;
    gap: ${space[3]};
  }

  .panel-head h3 {
    margin: 0;
    font: ${typeRole.body1Stronger.fontWeight} ${typeRole.body1Stronger.fontSize} /
      ${typeRole.body1Stronger.lineHeight} ${typeRole.body1Stronger.fontFamily};
    color: ${ink[1]};
  }

  .sub,
  .reason {
    margin: 0;
    font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
      ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
    color: ${ink[3]};
  }

  .derived-card {
    gap: ${space[2]};
  }

  .card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[2]};
  }

  .label {
    display: inline-flex;
    align-items: center;
    gap: ${space[2]};
    font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
      ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
    color: ${ink[3]};
  }

  .value {
    margin: 0;
    font: ${typeRole.subtitle2.fontWeight} ${typeRole.subtitle2.fontSize} /
      ${typeRole.subtitle2.lineHeight} ${typeRole.subtitle2.fontFamily};
    color: ${ink[1]};
  }

  .value.mono {
    font-family: ${fontMono};
  }

  .live {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
  }

  .actions {
    display: flex;
  }

  .next {
    margin-top: auto;
    padding-top: ${space[4]};
  }
`;
